// @flow

import { addTruthyParamsToUrl } from '@zapier/url-utils';
import {
  commonErrors as Errors,
  resourceNames,
} from '@zapier/toolbox-redux-framework';
import {
  fetch,
  fetchJson,
  postJson,
  putJson,
} from '@zapier/toolbox-browser-fetch';

import {
  ZAPIER_APP_API_PATH,
  ZAPIER_APP_V1_API_PATH,
} from 'app/developer-v3/constants';

import type {
  AppForm,
  ObjectType,
  OperationMethodType,
  PartialDefinition,
} from 'app/developer-v3/types';
import type { TestData } from 'app/developer-v3/components/RequestTester';

const Data = {};

Data.getAppFormFields = (formId: AppForm) => {
  const url = `/api/developer/v2/forms/${formId}`;
  return fetchJson(url);
};

Data.saveAppImage = (appId: number, imageFile: Object) => {
  const url = `${ZAPIER_APP_API_PATH}/${appId}/image`;

  const formData = new FormData();
  formData.append('image', imageFile);

  return fetch(url, { method: 'POST', body: formData })
    .then(fetch.success)
    .then(fetch.json);
};

Data.promoteAppVersion = async (
  appId: number,
  versionNumber: string,
  changelog?: string
) => {
  const url = `${ZAPIER_APP_API_PATH}/${appId}/versions/${versionNumber}/promote/production`;
  return putJson(url, {
    changelog,
  });
};

Data.deprecateAppVersion = async (
  appId: number,
  versionNumber: string,
  date: string
) => {
  const url = `${ZAPIER_APP_API_PATH}/${appId}/versions/${versionNumber}/deprecate`;
  return putJson(url, {
    deprecation_date: date,
  });
};

Data.migrateAppVersion = async (
  appId: number,
  fromVersionNumber: string,
  toVersionNumber: string,
  options: { percentage?: number, email?: string }
) => {
  const url = `${ZAPIER_APP_API_PATH}/${appId}/versions/${fromVersionNumber}/migrate-to/${toVersionNumber}`;
  const { percentage, email } = options;

  return postJson(url, { percent: percentage, user: email });
};

Data.cloneAppVersion = async (
  appId: number,
  fromVersionNumber: string,
  toVersionNumber: string
) => {
  const url = `${ZAPIER_APP_API_PATH}/${appId}/versions/${fromVersionNumber}/clone-to/${toVersionNumber}`;
  return postJson(url, { is_ui: true });
};

Data.executeTestRequest = (
  appId: number,
  versionNumber: string,
  objectType: ObjectType,
  objectKey: string,
  partialDefinition: PartialDefinition,
  authenticationId: string,
  operationMethod: OperationMethodType,
  testData: TestData
) => {
  const url = `${ZAPIER_APP_API_PATH}/${appId}/versions/${versionNumber}/test`;
  return postJson(url, {
    objectType,
    objectKey,
    partialDefinition,
    authenticationId,
    operationMethod,
    testData,
  });
};

Data.batchUpdateEnvironment = (
  appId: number,
  appVersion: string,
  env: Object
) => {
  const url = `${ZAPIER_APP_API_PATH}/${appId}/versions/${appVersion}/multi-environment`;
  return postJson(url, env);
};

Data.loadTasks = (queryParams: {}) => {
  const url = addTruthyParamsToUrl('/api/v3/stream', queryParams);
  return fetchJson(url).catch(errorResponse => {
    throw new Errors.DataGetError({
      resource: resourceNames.tasks,
      response: errorResponse,
    });
  });
};

Data.loadAppEvents = (appId: string | number, filters: {}) => {
  const apiUrl = `${ZAPIER_APP_V1_API_PATH}/apps/${appId}/events`;

  const url = fetch.url(apiUrl, filters);
  return fetchJson(url)
    .then(results => {
      return results?.events || [];
    })
    .catch(response => {
      throw new Errors.DataGetError({
        resource: resourceNames.devAppEvents,
        response,
      });
    });
};

export default Data;
